/**
 * useOrgRole — current user's organisation role
 *
 * Derives the role from the already-cached members list so no extra
 * API calls are made when the data is warm.
 *
 * Usage:
 *   const { isAdminOrOwner, role, isLoading } = useOrgRole();
 */

import { useAuth } from '@/contexts/AuthContext';
import { useCurrentOrganization } from './use-bss-organizations';
import { useOrganizationMembers } from './use-organization-members';

export type OrgRole = 'owner' | 'admin' | 'member' | 'viewer' | 'advisor';

export function useOrgRole() {
  const { user } = useAuth();
  const { data: orgData } = useCurrentOrganization();
  const orgId = orgData?.organization?.id ?? null;
  const { data: membersData, isLoading } = useOrganizationMembers(orgId);

  const member = membersData?.members.find((m) => m.user_id === user?.id);
  const role = (member?.role ?? null) as OrgRole | null;

  return {
    role,
    isLoading,
    isOwner: role === 'owner',
    isAdmin: role === 'admin',
    isAdminOrOwner: role === 'owner' || role === 'admin',
    isMember: role === 'member',
    isViewer: role === 'viewer',
    // A finance-only collaborator: Finance section only, every other menu
    // locked (enforced in the app shell — see useAdvisorLockdown).
    isAdvisor: role === 'advisor',
  };
}
